'use strict'

/**
 * Name: Day14 - Particle
 * ===========================================
 */

import * as PIXI from 'pixi.js'

/**
 * Dot Particle Class
 *
 * @constructor
 */
export class Particle {
  constructor(el, options = {}) {
    this.parentEl = el
    this.points = []
    this.app = false
    this.ratio = false

    this.containerLow = false

    this.sprites = []

    this.baseFillColor = 0xFFFFFF
    // this.particleColors = [0xff9326]
    this.particleColors = [0x32E0FE, 0xD761E9, 0x1379CB, 0x5C6CDE, 0x31EEDA]

    window.addEventListener('debounceResize', this.resize.bind(this), false)

    this.init()
  }

  /**
   * ランダムに値を取得する
   *
   * @static
   * @param {Number} min
   * @param {Number} max
   * @return {Number}
   */
  static UtilRandom(min = 0, max = 10) {
    return Math.floor((Math.random() * (max - min + 1)) + min)
  }

  /**
   * 配列内からランダムに値を取得する
   *
   * @static
   * @param {Array} array 配列
   * @return {Object} 配列内の値
   */
  static UtilRandomArray(array) {
    return array[Math.floor(Math.random() * array.length)]
  }

  /**
   * 少数点を第n位を切り捨てる
   *
   * @static
   * @param {Number} number
   * @param {Number} n - 第n位
   * @return {Number}
   */
  static UtilMathPow(number = 0, n = 5) {
    return Math.floor(number * Math.pow(10, n)) / Math.pow(10, n)
  }

  init() {
    const { width, height } = this.parentEl.getBoundingClientRect()
    this.app = new PIXI.Application(width, height, {
      transparent: true,
      resolution: 2,
      autoResize: true,
      antialias: true
    })
    this.parentEl.appendChild(this.app.view)

    this.w = width
    this.h = height
    this.count = Math.floor(this.w / 25)
    this.delta = 1
    this.ratio = width / height

    const particlesOptions = {
      scale: false,
      position: true,
      rotation: true,
      uvs: false,
      alpha: true
    }
    this.containerLow = new PIXI.particles.ParticleContainer(this.count, particlesOptions)
    this.app.stage.addChild(this.containerLow)

    const graphicsLevelLarge = new PIXI.Graphics()
    graphicsLevelLarge.beginFill(this.baseFillColor)
    graphicsLevelLarge.drawCircle(0, 0, this.w / 2)
    graphicsLevelLarge.endFill()
    graphicsLevelLarge.setTransform(0, 0, 0.7, 0.7)
    graphicsLevelLarge.alpha = 0.8
    graphicsLevelLarge._filters = [new PIXI.filters.BlurFilter(this.w / 2 / 8)]
    this.textureBlurLevelHigh = this.app.renderer.generateTexture(graphicsLevelLarge)

    for (let i = 0; i < this.count; i++) {
      this.initPoint(i)
    }

    for (let i = 0; i < this.points.length; i++) {
      const texture = this.textureBlurLevelHigh
      const s = new PIXI.Sprite(texture)
      s.width = this.points[i].size
      s.height = this.points[i].size
      s.position.x = this.points[i].x
      s.position.y = this.points[i].y
      s.anchor.x = 0.5
      s.anchor.y = 0.5
      s.alpha = this.points[i].alpha
      s.tint = this.constructor.UtilRandomArray(this.particleColors)
      this.sprites.push(s)
      this.containerLow.addChild(s)
    }

    this.containerLow.scale.set(1.2)
    this.containerLow.x = this.app.screen.width / 2
    this.containerLow.y = this.app.screen.height / 2
    this.containerLow.pivot.x = this.app.screen.width / 2
    this.containerLow.pivot.y = this.app.screen.height / 2

    this.app.ticker.add(delta => {
      // this.containerLow.rotation -= 0.0005
      this.updatePosition()
    }, PIXI.UPDATE_PRIORITY.LOW)
  }

  /**
   * ポイントの初期化
   */
  initPoint(index = 0) {
    const x = this.w * Math.random()
    const y = this.h * Math.random()
    const dx = -this.delta / 10 + this.delta * Math.random()
    const dy = -this.delta / 10 + this.delta * Math.random()

    const containerName = (index / this.count < 1 / (3 * 2)) ? 2 : (index / this.count < 1 / 3) ? 1 : 3
    const size = (containerName === 3) ? this.constructor.UtilRandom(this.w / 3, this.w / 3 * 2) : this.w / 3
    const blur = (containerName === 1) ? 'high' : (containerName === 2) ? 'normal' : 'low'
    const alpha = this.constructor.UtilRandom(7, 9) * 0.1
    const alphaChangeNumber = this.constructor.UtilRandom(5, 10) * 0.0001

    this.points.push({
      IS_ALPHA_UP: true,
      ALPHA_CHANGE_NUMBER: alphaChangeNumber,
      ALPHA_UP_LIMIT: alpha,
      ALPHA_DOWN_LIMIT: 0,
      x: x,
      y: y,
      dx: dx,
      dy: dy,
      size: size,
      blur: blur,
      alpha: alpha
    })
  }

  /**
   * ポイントの描画
   */
  drawPoint(point, i) {
    this.sprites[i].position.x = point.x
    this.sprites[i].position.y = point.y
  }

  /**
   * ポイントの位置変更
   */
  updatePosition() {
    for (let i = 0; i < this.points.length; i++) {
      // for Position Update
      let xDx = this.points[i].x + this.points[i].dx
      let yDy = this.points[i].y + this.points[i].dy
      if (xDx - this.points[i].size < -(this.w * 0.5) || xDx + this.points[i].size > (this.w * 1.5)) {
        this.points[i].dx = -this.points[i].dx
      }
      if (yDy - this.points[i].size < -(this.h * 0.5) || yDy + this.points[i].size > (this.h * 1.5)) {
        this.points[i].dy = -this.points[i].dy
      }
      this.points[i].x += this.points[i].dx
      this.points[i].y += this.points[i].dy
      //
      // // for Alpha Update
      // if (this.points[i].IS_ALPHA_UP) {
      //   this.points[i].alpha += this.points[i].ALPHA_CHANGE_NUMBER
      //   if (this.points[i].alpha > this.points[i].ALPHA_UP_LIMIT) {
      //     this.points[i].alpha = this.points[i].ALPHA_UP_LIMIT - this.points[i].ALPHA_CHANGE_NUMBER
      //     this.points[i].IS_ALPHA_UP = false
      //   }
      // } else {
      //   this.points[i].alpha -= this.points[i].ALPHA_CHANGE_NUMBER
      //   if (this.points[i].alpha < this.points[i].ALPHA_DOWN_LIMIT) {
      //     this.points[i].x = this.w * Math.random()
      //     this.points[i].y = this.h * Math.random()
      //     this.points[i].alpha = this.points[i].ALPHA_DOWN_LIMIT + this.points[i].ALPHA_CHANGE_NUMBER
      //     this.points[i].IS_ALPHA_UP = true
      //   }
      // }

      this.drawPoint(this.points[i], i)
    }
  }

  resize() {
    const { width, height } = this.parentEl.getBoundingClientRect()
    let w
    let h
    if (width / height >= this.ratio) {
      w = height * this.ratio
      h = height
    } else {
      w = width
      h = width / this.ratio
    }
    this.app.view.style.width = `${w}px`
    this.app.view.style.height = `${h}px`
  }

  destroy() {
    window.removeEventListener('debounceResize', this.resize.bind(this), false)
    this.app.destroy(true)
    this.app = null
  }
}
